/**
 * Pure logic for the tools a computer has said it cannot currently use.
 *
 * A tool that has spent its allowance is handed over quietly: the run goes to
 * another tool and the owner is told nothing, so work simply lands somewhere
 * else or takes longer. The record says which computer, which tool, why, and
 * when the tool comes back. This module decides which of those reports still
 * stand, the order they should be read in, and the words each one is read in.
 *
 * No I/O and no clock: the moment is passed in as `nowMs`, so the same rows
 * always read the same way.
 */

/** One row of the outage record, as the view hands it over. */
export interface RuntimeOutageLike {
  readonly node_id: string;
  readonly name: string;
  readonly runtime_key: string;
  readonly runtime_label: string | null;
  readonly reason_code: string;
  readonly observed_at: string;
  readonly available_from: string | null;
}

/** How an outage should read: a wait that passes, or something only the owner can undo. */
export type OutageTone = 'attention' | 'danger';

/** One outage in plain words, with everything the list needs already decided. */
export interface OutageLine {
  readonly key: string;
  readonly nodeId: string;
  readonly nodeName: string;
  readonly toolName: string;
  readonly reason: string;
  readonly returns: string;
  readonly text: string;
  readonly tone: OutageTone;
  readonly availableFromMs: number | null;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** The instant a tool is due back, or null when nobody said and the time cannot be read. */
export function availableFromMs(row: RuntimeOutageLike): number | null {
  if (row.available_from === null) return null;
  const ms = Date.parse(row.available_from);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Whether the report still stands. A report with no return time stands until a
 * run succeeds on the tool and clears it; one with a return time clears itself
 * the moment that time passes.
 */
export function isCurrent(row: RuntimeOutageLike, nowMs: number): boolean {
  const due = availableFromMs(row);
  return due === null || due > nowMs;
}

/** Why the tool is not being offered, in words rather than a code. */
export function reasonPhrase(reasonCode: string): string {
  if (reasonCode === 'out_of_usage') return 'has used up its allowance';
  if (reasonCode === 'signed_out') return 'is signed out';
  // Ran, was refused permission to do anything, and produced nothing. Unlike a spent
  // allowance this does not come back on its own - the way it is started has to change.
  if (reasonCode === 'misconfigured') return 'is not allowed to do anything on that computer';
  return `cannot be used (${reasonCode.replaceAll('_', ' ')})`;
}

/** Being signed out waits for the owner; a spent allowance only waits. */
export function toneFor(reasonCode: string): OutageTone {
  // Both of these wait for the owner rather than for time to pass.
  return reasonCode === 'signed_out' || reasonCode === 'misconfigured' ? 'danger' : 'attention';
}

/**
 * When the tool comes back, said the way a person would say it: roughly, and
 * plainly when nobody said at all.
 */
export function returnPhrase(dueMs: number | null, nowMs: number): string {
  if (dueMs === null) return 'return time not known';
  const delta = dueMs - nowMs;
  if (delta <= 0) return 'back now';
  if (delta < 90 * 1000) return 'back in under a minute';
  if (delta < 90 * MINUTE_MS) return `back in about ${Math.round(delta / MINUTE_MS)} minutes`;
  if (delta < 22 * HOUR_MS) {
    const hours = Math.round(delta / HOUR_MS);
    return `back in about ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  if (delta < 36 * HOUR_MS) return 'back tomorrow';
  return `back in about ${Math.round(delta / DAY_MS)} days`;
}

/** The tool as the owner knows it; a tool with no name of its own reads as its key. */
export function toolName(row: RuntimeOutageLike): string {
  const label = row.runtime_label;
  return label === null || label.trim() === '' ? row.runtime_key : label;
}

/** One row turned into the line that is read on the screen. */
export function outageLine(row: RuntimeOutageLike, nowMs: number): OutageLine {
  const due = availableFromMs(row);
  const tool = toolName(row);
  const reason = reasonPhrase(row.reason_code);
  const returns = returnPhrase(due, nowMs);
  return {
    key: `${row.node_id}::${row.runtime_key}`,
    nodeId: row.node_id,
    nodeName: row.name,
    toolName: tool,
    reason,
    returns,
    text: `${tool} on ${row.name} ${reason} — ${returns}.`,
    tone: toneFor(row.reason_code),
    availableFromMs: due,
  };
}

/**
 * The reports that still stand, soonest back first, and the ones nobody put a
 * time on last - they are the ones that need a person rather than a wait. Two
 * due at the same moment are read by computer, then by tool.
 */
export function currentOutages(rows: readonly RuntimeOutageLike[], nowMs: number): OutageLine[] {
  return rows
    .filter((row) => isCurrent(row, nowMs))
    .map((row) => outageLine(row, nowMs))
    .sort((a, b) => {
      if (a.availableFromMs === null && b.availableFromMs === null) {
        return a.nodeName.localeCompare(b.nodeName) || a.toolName.localeCompare(b.toolName);
      }
      if (a.availableFromMs === null) return 1;
      if (b.availableFromMs === null) return -1;
      return a.availableFromMs - b.availableFromMs
        || a.nodeName.localeCompare(b.nodeName)
        || a.toolName.localeCompare(b.toolName);
    });
}
